import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CheckCircle, Circle, MoreHorizontal,
  Briefcase, User, Scissors, HeartPulse, Zap,
  FileText, StickyNote, Trash2, XCircle, RotateCcw, Wand2,
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { TransactionDetailsModal } from '../components/TransactionDetailsModal';
import { InsuranceReimbursementModal } from '../components/InsuranceReimbursementModal';
import { TransferDetectionModal } from '../components/TransferDetectionModal';
import { SimpleRefundModal } from '../components/SimpleRefundModal';
import { CategorySubcategorySelect } from '../components/CategorySubcategorySelect';
import { SplitExpenseModal } from '../components/SplitExpenseModal';
import { RuleCreatorModal } from '../components/RuleCreatorModal';
import { MultiSelectCategoryDropdown } from '../components/MultiSelectCategoryDropdown';
import { MultiSelectAccountDropdown } from '../components/MultiSelectAccountDropdown';
import { MultiSelectStatusDropdown } from '../components/MultiSelectStatusDropdown';
import PDFImportModal from '../components/PDFImportModal';
import CSVImportModal from '../components/CSVImportModal';
import PDFDebugModal from '../components/PDFDebugModal';
import GroupedAccountSelect from '../components/GroupedAccountSelect';
import PortalDropdown from '../components/PortalDropdown';
import EmptyState from '../components/ui/EmptyState';
import { Account } from '../types';
import { filterTransactionsList } from './transactionListFilters';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  category_id: number | null;
  subcategory_id: number | null;
  is_transfer: boolean;
  is_refund?: boolean;
  refund_transaction_id?: number;
  confidence_score: number | null;
  linked_account_id: number;
  manual_account_id: number | null;
  account_name: string;
  category_name?: string;
  subcategory_name?: string;
  category_color?: string;
  // New verification fields
  is_verified?: boolean;
  verification_date?: string;
  // Categorization source tracking
  categorization_source?: 'manual' | 'rule' | 'system';
  categorized_by_rule_id?: number;
  // Insurance/reimbursement fields
  original_amount?: number;
  reimbursed_amount?: number;
  reimbursement_date?: string;
  insurance_provider?: string;
  is_reimbursable?: boolean;
  total_reimbursed_amount?: number;
  // Business expense tracking
  is_business_expense?: boolean;
  business_category?: string;
  // Notes
  notes?: string;
  // Splits
  splits?: any[];
  split_count?: number;
  // Post-categorize rule suggestion (populated from updateTransactionCategorization response)
  rule_suggestion?: {
    cluster_key: string;
    cluster_key_type: 'merchant' | 'description_prefix';
    count: number;
    category_id: number;
    subcategory_id: number | null;
    category_name: string;
    subcategory_name: string | null;
  } | null;
}

interface TransactionCategory {
  id: number;
  name: string;
  color: string;
  is_income?: boolean;
  subcategories: TransactionSubcategory[];
}

interface TransactionSubcategory {
  id: number;
  name: string;
}

/** Fallback when filter-accounts API fails: same logic as pre-pagination (first loaded page only). */
function transactionFilterAccountsFromLoadedTransactions(
  txns: Transaction[]
): { id: number; name: string }[] {
  const seen = new Map<string, number>();
  for (const t of txns) {
    if (!seen.has(t.account_name)) {
      seen.set(t.account_name, t.manual_account_id ?? t.linked_account_id);
    } else if (
      t.manual_account_id != null &&
      seen.get(t.account_name) !== t.manual_account_id
    ) {
      seen.set(t.account_name, t.manual_account_id);
    }
  }
  return Array.from(seen.entries())
    .map(([name, id]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface RuleCondition {
  id: number;
  field_type: string;
  operator: string;
  value: string;
  condition_order: number;
  logic_operator?: string;
}

interface CategorizationRuleSummary {
  id: number;
  rule_name: string;
  category_name?: string;
  subcategory_name?: string;
  conditions: RuleCondition[];
}

const Transactions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<TransactionCategory[]>([]);
  const [rulesById, setRulesById] = useState<Map<number, CategorizationRuleSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  
  // Transfer detection state
  const [transferModal, setTransferModal] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
    potentialTransfers: any[];
  }>({
    isOpen: false,
    transaction: null,
    potentialTransfers: []
  });

  // Refund picker state
  const [refundPickerTarget, setRefundPickerTarget] = useState<Transaction | null>(null);

  // Split expense state
  const [splitModal, setSplitModal] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
  }>({
    isOpen: false,
    transaction: null,
  });

  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{ date: string; id: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; ids: number[] }>({ isOpen: false, ids: [] });
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
    initialTab?: 'details' | 'notes';
  }>({ isOpen: false, transaction: null });
  
  const [insuranceModal, setInsuranceModal] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
  }>({ isOpen: false, transaction: null });


  const [ruleCreatorModal, setRuleCreatorModal] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
    prefillCategoryId?: number | null;
    prefillSubcategoryId?: number | null;
    prefillCondition?: { field_type: 'merchant' | 'description'; operator: 'equals' | 'starts_with'; value: string } | null;
  }>({ isOpen: false, transaction: null });
  
  // Status filters (multi-select)
  const [statusFilters, setStatusFilters] = useState<string[]>([]);

  // Inline table filters (multi-select where applicable)
  const [inlineFilters, setInlineFilters] = useState({
    dateRange: '',
    description: '',
    accounts: [] as string[],
    categories: [] as string[],
    amountMin: '',
    amountMax: '',
  });

  // Bulk categorize modal
  const [bulkCategorizeModal, setBulkCategorizeModal] = useState<{
    isOpen: boolean;
    categoryId: number | null;
    subcategoryId: number | null;
  }>({ isOpen: false, categoryId: null, subcategoryId: null });


  // Track transactions that were visible when filter was applied (for sticky filtering)
  const [stickyVisibleTransactions, setStickyVisibleTransactions] = useState<Set<number>>(new Set());

  // Stats
  const [stats, setStats] = useState({
    totalBalance: 0,
    totalTransactions: 0,
    uncategorizedCount: 0
  });

  // Import UI state
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState<Account[]>([]);
  const [transactionFilterAccounts, setTransactionFilterAccounts] = useState<
    { id: number; name: string }[]
  >([]);

  // PDF Import UI state
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const [pdfDebugOpen, setPdfDebugOpen] = useState(false);

  // Tracks the currently open PortalDropdown so modal-opening actions can close it first
  const activeDropdownRef = useRef<{ close(): void } | null>(null);
  const closeActiveDropdown = () => {
    activeDropdownRef.current?.close();
    activeDropdownRef.current = null;
  };

  const loadAccounts = async () => {
    try {
      const accountsData = await api.accounts.getAccounts();
      // Defensive dedupe: API should return one row per manual account,
      // but keep dropdown stable if stale duplicate rows exist.
      const deduped = accountsData.filter(
        (account: Account, index: number, self: Account[]) =>
          index === self.findIndex((a) => a.id === account.id)
      );
      setAvailableAccounts(deduped);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Filter transactions with useMemo to prevent infinite re-renders
  const filteredTransactions = useMemo(() => {
    const typeFilter = searchParams.get('type');
    return filterTransactionsList(transactions, {
      typeFilter,
      statusFilters,
      inlineFilters,
      stickyVisibleTransactions,
    });
  }, [transactions, statusFilters, inlineFilters, searchParams, stickyVisibleTransactions]);

  useEffect(() => {
    // Calculate stats when filtered transactions change
    const totalBalance = filteredTransactions.reduce((sum, t) => sum + t.amount, 0);
    const uncategorizedCount = filteredTransactions.filter(t =>
      !t.category_id && !(t.splits && t.splits.length > 0 && t.splits.some((s: any) => s.category_id))
    ).length;

    setStats({
      totalBalance,
      totalTransactions: filteredTransactions.length,
      uncategorizedCount
    });
  }, [filteredTransactions]);

  // Track the previous category filter to detect changes
  const [previousCategoryFilter, setPreviousCategoryFilter] = useState<string>('');

  // Update sticky visible transactions when category filter changes
  useEffect(() => {
    const categoriesFilterString = inlineFilters.categories.join(',');
    
    if (inlineFilters.categories.length > 0) {
      // Only capture sticky transactions when filter is first applied or changed
      if (previousCategoryFilter !== categoriesFilterString) {
        // Get transactions that match the new filter (without sticky behavior)
        const newFilteredTransactions = transactions.filter(transaction => {
          for (const categoryFilter of inlineFilters.categories) {
            if (categoryFilter === 'uncategorized') {
              // Uncategorized: no direct category AND no splits
              const hasDirectCategory = !!transaction.category_id;
              const hasSplits = transaction.splits && transaction.splits.length > 0;
              if (!hasDirectCategory && !hasSplits) return true;
            } else if (categoryFilter.startsWith('cat-')) {
              const categoryId = parseInt(categoryFilter.replace('cat-', ''));
              const hasDirectMatch = transaction.category_id === categoryId;
              const hasSplitMatch = transaction.splits && transaction.splits.some((split: any) => split.category_id === categoryId);
              if (hasDirectMatch || hasSplitMatch) return true;
            } else if (categoryFilter.startsWith('sub-')) {
              const subcategoryId = parseInt(categoryFilter.replace('sub-', ''));
              const hasDirectMatch = transaction.subcategory_id === subcategoryId;
              const hasSplitMatch = transaction.splits && transaction.splits.some((split: any) => split.subcategory_id === subcategoryId);
              if (hasDirectMatch || hasSplitMatch) return true;
            }
          }
          return false;
        });
        
        const visibleIds = new Set(newFilteredTransactions.map(t => t.id));
        setStickyVisibleTransactions(visibleIds);
        setPreviousCategoryFilter(categoriesFilterString);
      }
    } else {
      // When no category filter is applied, clear the sticky set
      setStickyVisibleTransactions(new Set());
      setPreviousCategoryFilter('');
    }
  }, [inlineFilters.categories, transactions, previousCategoryFilter]);

  const loadData = async (preserveScrollPosition = false) => {
    const scrollPosition = preserveScrollPosition ? window.scrollY : null;

    try {
      setLoading(true);
      setLoadError(null);

      const [txResponse, categoriesData, accountsData, rulesData] = await Promise.all([
        api.transactions.getTransactions(),
        api.transactions.getCategories(),
        api.accounts.getAccounts(),
        api.transactions.getRules().catch(() => []),
      ]);

      // Response is { transactions, hasMore, nextCursor }
      const txData: Transaction[] = txResponse?.transactions ?? [];
      const more: boolean = txResponse?.hasMore ?? false;
      const cursor = txResponse?.nextCursor ?? null;

      setTransactions(txData);
      setHasMore(more);
      setNextCursor(cursor);
      setCategories(categoriesData);

      const rMap = new Map<number, CategorizationRuleSummary>();
      for (const rule of (rulesData ?? [])) {
        rMap.set(rule.id, rule);
      }
      setRulesById(rMap);
      setAvailableAccounts(
        accountsData.filter(
          (account: Account, index: number, self: Account[]) =>
            index === self.findIndex((a) => a.id === account.id)
        )
      );

      try {
        const fa = await api.transactions.getTransactionFilterAccounts();
        const list = Array.isArray(fa?.accounts) ? fa.accounts : [];
        setTransactionFilterAccounts(list);
      } catch (filterAccErr) {
        console.error('Error loading transaction filter accounts:', filterAccErr);
        setTransactionFilterAccounts(transactionFilterAccountsFromLoadedTransactions(txData));
      }

      if (preserveScrollPosition && scrollPosition !== null) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPosition);
          });
        });
      }
    } catch (err) {
      console.error('Error loading transaction data:', err);
      setLoadError('Failed to load transaction data');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTransactions = async () => {
    if (!hasMore || loadingMore || !nextCursor) return;
    try {
      setLoadingMore(true);
      const moreResponse = await api.transactions.getTransactions({
        cursorDate: nextCursor.date,
        cursorId: nextCursor.id,
      });
      const moreTx: Transaction[] = moreResponse?.transactions ?? [];
      setTransactions(prev => [...prev, ...moreTx]);
      setHasMore(moreResponse?.hasMore ?? false);
      setNextCursor(moreResponse?.nextCursor ?? null);
    } catch (err) {
      console.error('Error loading more transactions:', err);
      setActionError('Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSyncTransactions = async () => {
    if (!user?.plaid_enabled) {
      setActionError('Plaid sync is not enabled on your account. Connect a bank account to enable automatic syncing.');
      return;
    }
    try {
      setSyncing(true);
      await api.plaid.syncAllTransactions();
      await loadData();
    } catch (err) {
      console.error('Error syncing transactions:', err);
      setActionError('Failed to sync transactions');
    } finally {
      setSyncing(false);
    }
  };


  const handleCategoryUpdate = async (transactionId: number, categoryId: number | null, subcategoryId?: number | null) => {
    try {
      // Find the current transaction to check if it's a refund
      const currentTransaction = transactions.find(t => t.id === transactionId);
      const isCurrentlyRefund = currentTransaction?.is_refund;
      
      // Check if the new category is a refund category
      const newCategory = categories.find(c => c.id === categoryId);
      const isNewRefundCategory = newCategory?.name?.toLowerCase().includes('refund') || 
                                 newCategory?.name?.toLowerCase().startsWith('refund for');
      
      // If changing from refund to non-refund, clean up refund relationships
      if (isCurrentlyRefund && !isNewRefundCategory) {
        await api.transactions.clearRefund(transactionId);
      }
      
      const updated = await api.transactions.updateTransactionCategorization(transactionId, {
        category_id: categoryId || undefined,
        subcategory_id: subcategoryId || undefined
      });

      // Update local state, merging the server's rule_suggestion into the transaction
      setTransactions(prev => prev.map(t => {
        if (t.id === transactionId) {
          const category = categories.find(c => c.id === categoryId);
          const subcategory = category?.subcategories.find(s => s.id === subcategoryId);
          return {
            ...t,
            category_id: categoryId,
            subcategory_id: subcategoryId || null,
            category_name: category?.name,
            subcategory_name: subcategory?.name,
            category_color: category?.color,
            categorization_source: 'manual',
            rule_suggestion: updated?.rule_suggestion ?? null,
            // Clear refund status if changing to non-refund category
            is_refund: isNewRefundCategory ? t.is_refund : false
          };
        }
        return t;
      }));
    } catch (err) {
      console.error('Error updating transaction:', err);
      setActionError('Failed to update transaction');
    }
  };

  const handleUnlinkRefund = async (transactionId: number) => {
    try {
      await api.transactions.clearRefund(transactionId);
      setTransactions(prev => prev.map(t => {
        if (t.id === transactionId) {
          return { ...t, is_refund: false, category_id: null, category_name: undefined, subcategory_id: null, subcategory_name: undefined, refund_transaction_id: undefined };
        }
        return t;
      }));
    } catch (err) {
      console.error('Error unlinking refund:', err);
      setActionError('Failed to unlink refund');
    }
  };

  const handleTransferDetection = async (transaction: Transaction) => {
    try {
      // Find potential transfer matches
      const result = await api.transactions.findTransferMatches(transaction.id);
      
      setTransferModal({
        isOpen: true,
        transaction: result.sourceTransaction,
        potentialTransfers: result.potentialMatches
      });
    } catch (err) {
      console.error('Error finding transfer matches:', err);
      setActionError('Failed to find transfer matches');
    }
  };

  const handleConfirmTransfer = async (transfer: { id: number; linked_account_id: number }) => {
    if (!transferModal.transaction) return;

    try {
      const result = await api.transactions.confirmTransfer(transferModal.transaction.id, transfer.linked_account_id, transfer.id);

      const confirmedId = transferModal.transaction.id;
      setTransferModal({ isOpen: false, transaction: null, potentialTransfers: [] });

      // Find the Transfer category from already-loaded categories so the row
      // reflects the correct category name + id without a full page refresh.
      const transferCategory = categories.find(c => c.name === 'Transfer');
      const transferUpdates = {
        is_transfer: true,
        category_id: transferCategory?.id ?? null,
        category_name: transferCategory ? 'Transfer' : undefined,
        subcategory_id: null,
        subcategory_name: undefined,
      };
      setTransactions(prev => prev.map(t =>
        t.id === confirmedId || t.id === result.targetTransaction.id
          ? { ...t, ...transferUpdates }
          : t
      ));
    } catch (err) {
      console.error('Error confirming transfer:', err);
      setActionError('Failed to confirm transfer');
    }
  };

  const handleRefundCategoryPicked = async (
    transaction: Transaction,
    categoryId: number,
    subcategoryId: number | null
  ) => {
    try {
      const updated = await api.transactions.markAsRefund(transaction.id, {
        category_id: categoryId,
        subcategory_id: subcategoryId,
      });
      setTransactions(prev =>
        prev.map(t => t.id === transaction.id ? { ...t, ...updated } : t)
      );
    } catch (error) {
      console.error('Error marking refund:', error);
    } finally {
      setRefundPickerTarget(null);
    }
  };


  const handleVerificationToggle = async (transactionId: number, isVerified: boolean) => {
    // Find the transaction to check if it's properly categorized
    const transaction = transactions.find(t => t.id === transactionId);
    
    // Check if transaction is categorized (either directly or via splits)
    const hasCategory = transaction?.category_id !== null && transaction?.category_id !== undefined;
    const hasSplitsWithCategories = transaction?.splits && transaction.splits.length > 0 && 
      transaction.splits.some((split: any) => split.category_id);
    const isCategorized = hasCategory || hasSplitsWithCategories;
    
    // Don't allow verification of uncategorized transactions
    if (isVerified && transaction && !isCategorized) {
      setActionError('Cannot verify uncategorized transactions. Please assign a category first.');
      return;
    }

    try {
      await api.transactions.updateTransactionVerification(transactionId, {
        is_verified: isVerified,
      });
      
      // Update local state
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { 
              ...t, 
              is_verified: isVerified,
              verification_date: isVerified ? new Date().toISOString() : undefined
            }
          : t
      ));
    } catch (error) {
      console.error('Error updating verification:', error);
      setActionError('Failed to update transaction verification');
    }
  };

  const handleBusinessToggle = async (transactionId: number, isBusiness: boolean) => {
    try {
      await api.transactions.updateTransactionBusinessFlag(transactionId, {
        is_business_expense: isBusiness,
        business_category: isBusiness ? 'General' : undefined,
      });
      
      // Update local state
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { 
              ...t, 
              is_business_expense: isBusiness,
              business_category: isBusiness ? 'General' : undefined
            }
          : t
      ));
    } catch (error) {
      console.error('Error updating business flag:', error);
      setActionError('Failed to update business expense flag');
    }
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === filteredTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const handleSelectTransaction = (transactionId: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleOpenDetails = (transaction: Transaction, initialTab: 'details' | 'notes' = 'details') => {
    closeActiveDropdown();
    setDetailsModal({
      isOpen: true,
      transaction,
      initialTab,
    });
  };

  const handleCloseDetails = () => {
    setDetailsModal({
      isOpen: false,
      transaction: null,
    });
  };

  // Modal close handlers that preserve scroll position
  const handleCloseTransferModal = () => {
    setTransferModal({ isOpen: false, transaction: null, potentialTransfers: [] });
  };

  const handleCloseRefundPicker = () => {
    setRefundPickerTarget(null);
  };

  const handleTransactionUpdate = (updatedTransaction: Transaction) => {
    setTransactions(prev => prev.map(t => 
      t.id === updatedTransaction.id ? updatedTransaction : t
    ));
    // Also update the modal's transaction if it's currently open for this transaction
    if (detailsModal.isOpen && detailsModal.transaction?.id === updatedTransaction.id) {
      setDetailsModal({
        ...detailsModal,
        transaction: updatedTransaction,
      });
    }
  };

  const handleDeleteTransaction = (transactionId: number) => {
    closeActiveDropdown();
    setDeleteConfirm({ isOpen: true, ids: [transactionId] });
  };

  const handleBulkDelete = () => {
    if (selectedTransactions.size === 0) return;
    setDeleteConfirm({ isOpen: true, ids: Array.from(selectedTransactions) });
  };

  const handleConfirmDelete = async () => {
    const { ids } = deleteConfirm;
    setDeleteConfirm({ isOpen: false, ids: [] });
    try {
      await Promise.all(ids.map(id => api.transactions.deleteTransaction(id)));
      setTransactions(prev => prev.filter(t => !ids.includes(t.id)));
      setSelectedTransactions(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    } catch (error) {
      console.error('Error deleting transactions:', error);
      setActionError('Failed to delete transaction(s)');
    }
  };

  const handleBulkCategorize = async (categoryId: number | null, subcategoryId: number | null) => {
    if (selectedTransactions.size === 0 || categoryId === null) return;
    try {
      await api.transactions.bulkReassignTransactions({
        transaction_ids: Array.from(selectedTransactions),
        new_category_id: categoryId,
        new_subcategory_id: subcategoryId ?? undefined,
      });
      const category = categories.find(c => c.id === categoryId);
      const subcategory = category?.subcategories.find(s => s.id === subcategoryId);
      setTransactions(prev => prev.map(t =>
        selectedTransactions.has(t.id)
          ? {
              ...t,
              category_id: categoryId,
              subcategory_id: subcategoryId ?? null,
              category_name: category?.name,
              subcategory_name: subcategory?.name,
              category_color: category?.color,
              categorization_source: 'manual',
            }
          : t
      ));
      setBulkCategorizeModal({ isOpen: false, categoryId: null, subcategoryId: null });
      setSelectedTransactions(new Set());
    } catch (error) {
      console.error('Error bulk categorizing:', error);
      setActionError('Failed to bulk categorize transactions');
    }
  };

  const handleBulkVerify = async (isVerified: boolean) => {
    if (selectedTransactions.size === 0) return;
    const selectedIds = Array.from(selectedTransactions);
    try {
      const result = await api.transactions.bulkVerifyTransactions({
        transaction_ids: selectedIds,
        is_verified: isVerified,
      });
      setTransactions(prev => prev.map(t =>
        selectedTransactions.has(t.id) && (t.category_id || (t.splits && t.splits.length > 0))
          ? { ...t, is_verified: isVerified, verification_date: isVerified ? new Date().toISOString() : undefined }
          : t
      ));
      setSelectedTransactions(new Set());
      if (result?.updated_count != null && result.updated_count < selectedIds.length) {
        setActionMessage(`${result.updated_count} of ${selectedIds.length} transactions verified — the rest were skipped (uncategorized)`);
      }
    } catch (error) {
      console.error('Error bulk verifying:', error);
      setActionError('Failed to bulk verify transactions');
    }
  };

  const handleExportCSV = useCallback(() => {
    const rows = filteredTransactions.map(t => [
      t.date,
      t.merchant_name || t.description,
      t.description,
      t.account_name,
      t.category_name || '',
      t.subcategory_name || '',
      t.amount.toString(),
      t.is_verified ? 'Verified' : 'Unverified',
      t.is_business_expense ? 'Business' : 'Personal',
      t.notes || '',
    ]);
    const header = ['Date', 'Merchant', 'Description', 'Account', 'Category', 'Subcategory', 'Amount', 'Status', 'Type', 'Notes'];
    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredTransactions]);

  const handleOpenInsuranceModal = (transaction: Transaction) => {
    closeActiveDropdown();
    setInsuranceModal({
      isOpen: true,
      transaction,
    });
  };

  const handleCloseInsuranceModal = () => {
    setInsuranceModal({
      isOpen: false,
      transaction: null,
    });
  };

  // Account filter: only accounts that have ≥1 transaction (server aggregates all pages).
  const accounts = useMemo(() => {
    return [...transactionFilterAccounts].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [transactionFilterAccounts]);

  // Separate income and expense categories using the is_income flag from the DB.
  // Transfer appears in both lists. is_income is now saved on create/update so
  // user-created categories are correctly classified.
  const incomeCategories = categories.filter(c => c.is_income === true || c.name === 'Transfer');
  const expenseCategories = categories.filter(c => c.is_income !== true || c.name === 'Transfer');

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading transactions...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="transactions-page">
        <div className="error-message">
          <p>{loadError}</p>
          <button onClick={() => loadData()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      {/* Header */}
      <div className="transactions-header">
        <h1>
          {searchParams.get('type') === 'income' ? '📈 Income Transactions' :
           searchParams.get('type') === 'expense' ? '📉 Expense Transactions' :
           'Transactions'}
        </h1>
        <div className="header-actions">
          {user?.plaid_enabled && (
            <button
              onClick={handleSyncTransactions}
              disabled={syncing}
              className="btn btn-secondary"
            >
              {syncing ? 'Syncing...' : 'Sync'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => { loadAccounts(); setCsvImportOpen(true); }}>Import CSV</button>
          <button className="btn btn-secondary" onClick={() => {
            if (availableAccounts.length === 0) loadAccounts();
            setPdfImportOpen(true);
          }}>
            📄 Import PDF
          </button>
          {import.meta.env.DEV && (
            <button className="btn btn-secondary" onClick={() => {
              if (availableAccounts.length === 0) loadAccounts();
              setPdfDebugOpen(true);
            }}>
              🔍 Debug PDF
            </button>
          )}
        </div>
      </div>

      {/* Inline action error banner */}
      {actionError && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', marginBottom: '12px',
          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: '8px', color: 'var(--danger-color)', fontSize: '14px',
        }}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* Inline action info message */}
      {actionMessage && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', marginBottom: '12px',
          background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.4)',
          borderRadius: '8px', color: 'var(--text-secondary)', fontSize: '14px',
        }}>
          <span>{actionMessage}</span>
          <button onClick={() => setActionMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1 }}>✕</button>
        </div>
      )}




      {/* Filters and Search */}
      <div className="transactions-controls">
        <div className="bulk-actions">
          <input
            type="checkbox"
            checked={selectedTransactions.size === filteredTransactions.length && filteredTransactions.length > 0}
            onChange={handleSelectAll}
            className="select-all-checkbox"
          />
          <span>Select all</span>
          <div className="action-icons">
            <button
              className="action-icon"
              title="Delete selected"
              disabled={selectedTransactions.size === 0}
              onClick={handleBulkDelete}
            >
              🗑️
            </button>
            <button
              className="action-icon"
              title="Bulk categorize selected"
              disabled={selectedTransactions.size === 0}
              onClick={() => setBulkCategorizeModal({ isOpen: true, categoryId: null, subcategoryId: null })}
            >
              🏷️
            </button>
            <button
              className="action-icon"
              title="Verify selected"
              disabled={selectedTransactions.size === 0}
              onClick={() => handleBulkVerify(true)}
            >
              ✅
            </button>
            <button
              className="action-icon"
              title="Export filtered transactions to CSV"
              onClick={handleExportCSV}
            >
              📤
            </button>
          </div>
        </div>

        <div className="filter-controls">
          <MultiSelectStatusDropdown
            selectedValues={statusFilters}
            onChange={setStatusFilters}
            placeholder="Filter by status..."
            className="status-filter"
          />
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              <th style={{width: "40px"}}></th>
              <th style={{width: "50px"}}>ID</th>
              <th>Date</th>
              <th>Description</th>
              <th>Account</th>
              <th colSpan={2}>Category</th>
              <th style={{textAlign: 'right'}}>Amount</th>
              <th>Actions</th>
            </tr>
            <tr className="table-filter-row">
              <th style={{width: "40px"}}></th>
              <th style={{width: "50px"}}></th>
              <th><input type="text" placeholder="Date..." value={inlineFilters.dateRange} onChange={(e) => setInlineFilters(prev => ({ ...prev, dateRange: e.target.value }))} className="filter-input" /></th>
              <th><input type="text" placeholder="Description..." value={inlineFilters.description} onChange={(e) => setInlineFilters(prev => ({ ...prev, description: e.target.value }))} className="filter-input" /></th>
              <th>
                <MultiSelectAccountDropdown
                  accounts={accounts}
                  selectedValues={inlineFilters.accounts}
                  onChange={(values) => setInlineFilters(prev => ({ ...prev, accounts: values }))}
                  placeholder="All accounts"
                  className="account-filter"
                />
              </th>
              <th colSpan={2}>
                <MultiSelectCategoryDropdown
                  categories={categories}
                  selectedValues={inlineFilters.categories}
                  onChange={(values) => setInlineFilters(prev => ({ ...prev, categories: values }))}
                  placeholder="All categories"
                  className="category-filter"
                />
              </th>
              <th style={{textAlign: 'right'}}>
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                  <input
                    type="number"
                    placeholder="Min $"
                    value={inlineFilters.amountMin}
                    onChange={(e) => setInlineFilters(prev => ({ ...prev, amountMin: e.target.value }))}
                    className="filter-input"
                    style={{ width: '70px' }}
                  />
                  <input
                    type="number"
                    placeholder="Max $"
                    value={inlineFilters.amountMax}
                    onChange={(e) => setInlineFilters(prev => ({ ...prev, amountMax: e.target.value }))}
                    className="filter-input"
                    style={{ width: '70px' }}
                  />
                </div>
              </th>
              <th><button onClick={() => {
                setInlineFilters({ dateRange: '', description: '', accounts: [], categories: [], amountMin: '', amountMax: '' });
                setStatusFilters([]);
                setStickyVisibleTransactions(new Set());
              }} className="clear-filters-btn" title="Clear all filters">✕</button></th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((transaction) => {
              // A transaction is uncategorized if:
              // 1. It has no direct category_id AND
              // 2. It has no splits with categories
              const hasCategory = transaction.category_id !== null && transaction.category_id !== undefined;
              const hasSplitsWithCategories = transaction.splits && transaction.splits.length > 0 && 
                transaction.splits.some((split: any) => split.category_id);
              const isUncategorized = !hasCategory && !hasSplitsWithCategories;
              const isIncome = transaction.amount > 0;
              const isSelected = selectedTransactions.has(transaction.id);
              
              return (
                <tr 
                  key={transaction.id} 
                  className={`transaction-row ${isUncategorized ? 'uncategorized' : ''} ${isSelected ? 'selected' : ''} ${transaction.is_verified ? 'verified' : ''} ${transaction.is_business_expense ? 'business-expense' : ''} ${transaction.categorization_source === 'rule' || transaction.categorization_source === 'system' ? 'auto-categorized' : ''}`}
                  onMouseEnter={(e) => e.currentTarget.classList.add('hover')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('hover')}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectTransaction(transaction.id)}
                      className="row-checkbox"
                    />
                  </td>
                  
                  <td className="id-cell" style={{fontSize: '12px', color: '#666'}}>
                    {transaction.id}
                  </td>
                  
                  <td className="date-cell">
                    {formatDate(transaction.date)}
                  </td>
                  
                  <td className="description-cell">
                    <div className="description-content">
                      <span className={`description-text ${isUncategorized ? 'uncategorized-text' : ''}`}>
                        {transaction.merchant_name || transaction.description}
                      </span>
                      {transaction.merchant_name && transaction.merchant_name !== transaction.description && (
                        <span className="secondary-description">
                          {transaction.description}
                        </span>
                      )}
                      {(transaction.is_business_expense || (isIncome && transaction.is_reimbursable)) && (
                        <div className="row-tags">
                          {transaction.is_business_expense && (
                            <span className="row-tag business-tag">Business</span>
                          )}
                          {isIncome && transaction.is_reimbursable && (
                            <span className="row-tag insurance-tag">Insurance</span>
                          )}
                        </div>
                      )}
                      {transaction.notes && (
                        <div className="note-snippet">
                          <StickyNote size={11} />
                          <span>{transaction.notes}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="account-cell">
                    {isUncategorized ? (
                      <div className="uncategorized-highlight">
                        {transaction.account_name}
                      </div>
                    ) : (
                      <span>{transaction.account_name}</span>
                    )}
                  </td>
                  
                  <td className="category-cell" colSpan={2}>
                    {transaction.splits && transaction.splits.length > 0 ? (
                      <div className="split-categories-display">
                        <div className="split-indicator" style={{ 
                          fontSize: '11px', 
                          color: 'var(--text-secondary)', 
                          marginBottom: '4px',
                          fontWeight: 'bold'
                        }}>
                          Split ({transaction.splits.length}):
                        </div>
                        <div className="split-list" style={{ fontSize: '12px' }}>
                          {transaction.splits.map((split: any, idx: number) => (
                            <div key={split.id || idx} style={{ marginBottom: '2px' }}>
                              <span style={{
                                color: 'var(--success-color)',
                                fontWeight: '500'
                              }}>
                                {split.category_name}
                                {split.subcategory_name && ` - ${split.subcategory_name}`}
                              </span>
                              <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                                ({formatCurrency(split.amount)})
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => setSplitModal({ isOpen: true, transaction })}
                          style={{
                            marginTop: '4px',
                            fontSize: '11px',
                            padding: '4px 8px',
                            background: 'var(--background-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          Manage Splits
                        </button>
                      </div>
                    ) : transaction.is_refund ? (
                      <div className="refund-category-label">
                        <span className="refund-category-name">
                          {transaction.category_name || 'Refund'}
                          {transaction.subcategory_name && ` · ${transaction.subcategory_name}`}
                        </span>
                        <span className="refund-category-badge">refund</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CategorySubcategorySelect
                          categories={isIncome ? incomeCategories : expenseCategories}
                          currentCategoryId={transaction.category_id}
                          currentSubcategoryId={transaction.subcategory_id}
                          isIncome={isIncome}
                          isRefund={transaction.is_refund}
                          placeholder={isUncategorized ? `Uncategorized ${isIncome ? 'income' : 'expense'}` : 'Search categories...'}
                          className={isUncategorized ? 'uncategorized-select' : ''}
                          onChange={async (categoryId, subcategoryId) => {
                            if (categoryId === -2) {
                              // Transfer
                              await handleTransferDetection(transaction);
                            } else if (categoryId === -3) {
                              // Refund
                              setRefundPickerTarget(transaction);
                            } else {
                              // Regular category update
                              handleCategoryUpdate(transaction.id, categoryId, subcategoryId);
                            }
                          }}
                        />
                        {transaction.categorization_source === 'rule' && transaction.categorized_by_rule_id != null && (() => {
                          const rule = rulesById.get(transaction.categorized_by_rule_id!);
                          return (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      color: 'var(--text-muted)',
                                      cursor: 'default',
                                      flexShrink: 0,
                                      opacity: 0.7,
                                    }}
                                  >
                                    <Wand2 size={12} />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  style={{
                                    background: 'var(--background-card)',
                                    border: '1px solid var(--border-color)',
                                    color: 'var(--text-primary)',
                                    fontSize: '12px',
                                    lineHeight: '1.6',
                                    maxWidth: '300px',
                                    padding: '8px 10px',
                                  }}
                                >
                                  {rule ? (
                                    <div>
                                      <div style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Auto-categorized by rule:
                                      </div>
                                      {rule.conditions.length > 0 ? (
                                        <div style={{ marginBottom: '6px' }}>
                                          {rule.conditions.map((c: RuleCondition, i: number) => (
                                            <div key={c.id} style={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap' }}>
                                              {i > 0 && (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600 }}>
                                                  {c.logic_operator ?? 'AND'}
                                                </span>
                                              )}
                                              <span style={{ color: 'var(--text-secondary)' }}>{c.field_type}</span>
                                              <span style={{ color: 'var(--text-muted)' }}>{c.operator.replace(/_/g, ' ')}</span>
                                              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>&ldquo;{c.value}&rdquo;</span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div style={{ color: 'var(--text-muted)', marginBottom: '6px', fontStyle: 'italic' }}>
                                          {rule.rule_name}
                                        </div>
                                      )}
                                      <div style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', paddingTop: '4px', marginTop: '2px' }}>
                                        → {rule.category_name}{rule.subcategory_name ? ` — ${rule.subcategory_name}` : ''}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)' }}>Auto-categorized by rule</span>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                      </div>
                      {!isUncategorized && !transaction.is_verified && transaction.rule_suggestion && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                          <Wand2 size={10} style={{ flexShrink: 0, color: 'var(--primary-color)', opacity: 0.7 }} />
                          <span>{transaction.rule_suggestion.count} similar uncategorized</span>
                          <span style={{ color: 'var(--border-color)' }}>—</span>
                          <button
                            onClick={() => setRuleCreatorModal({
                              isOpen: true,
                              transaction,
                              prefillCategoryId: transaction.rule_suggestion!.category_id,
                              prefillSubcategoryId: transaction.rule_suggestion!.subcategory_id,
                              prefillCondition: {
                                field_type: transaction.rule_suggestion!.cluster_key_type === 'merchant' ? 'merchant' : 'description',
                                operator: transaction.rule_suggestion!.cluster_key_type === 'merchant' ? 'equals' : 'starts_with',
                                value: transaction.rule_suggestion!.cluster_key,
                              },
                            })}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color)', cursor: 'pointer', fontSize: '11px', fontWeight: 500, textDecoration: 'underline', flexShrink: 0 }}
                          >
                            Create rule
                          </button>
                          <span style={{ color: 'var(--border-color)' }}>·</span>
                          <button
                            onClick={() => {
                              setInlineFilters(prev => ({ ...prev, description: transaction.rule_suggestion!.cluster_key }));
                              setTransactions(prev => prev.map(t => t.id === transaction.id ? { ...t, rule_suggestion: null } : t));
                            }}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', flexShrink: 0 }}
                          >
                            Review
                          </button>
                          <span style={{ color: 'var(--border-color)' }}>·</span>
                          <button
                            onClick={() => setTransactions(prev => prev.map(t => t.id === transaction.id ? { ...t, rule_suggestion: null } : t))}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', flexShrink: 0 }}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                      </div>
                    )}
                  </td>
                  
                  <td className={`amount-cell ${isIncome ? 'income' : 'expense'}`}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <span className={`amount ${isUncategorized ? 'uncategorized-amount' : ''}`}>
                        {formatCurrency(transaction.amount)}
                      </span>
                      {!isIncome && transaction.total_reimbursed_amount && transaction.total_reimbursed_amount > 0 && (
                        <span
                          className="row-tag insurance-tag"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                          title={`Reimbursed: ${formatCurrency(transaction.total_reimbursed_amount)} of ${formatCurrency(Math.abs(transaction.amount))}`}
                        >
                          <HeartPulse size={10} />
                          {transaction.total_reimbursed_amount >= Math.abs(transaction.amount) ? 'Fully reimbursed' : `${formatCurrency(transaction.total_reimbursed_amount)} reimbursed`}
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="actions-cell">
                    <div className="action-controls">
                      {/* Verification status indicator — always visible, clickable */}
                      {(() => {
                        const isCategorized = hasCategory || hasSplitsWithCategories;
                        const isAuto = transaction.categorization_source === 'rule' || transaction.categorization_source === 'system';
                        const statusClass = transaction.is_verified ? 'tx-status verified'
                          : !isCategorized ? 'tx-status needs-category'
                          : isAuto ? 'tx-status auto'
                          : 'tx-status unverified';
                        const statusTitle = !isCategorized ? 'Assign a category to verify'
                          : transaction.is_verified ? 'Verified — click to unverify'
                          : isAuto ? 'Auto-categorized — click to verify'
                          : 'Unverified — click to verify';
                        return (
                          <button
                            className={statusClass}
                            onClick={() => isCategorized && handleVerificationToggle(transaction.id, !transaction.is_verified)}
                            disabled={!isCategorized}
                          >
                            {transaction.is_verified
                              ? <CheckCircle size={15} />
                              : isAuto
                              ? <CheckCircle size={15} style={{ opacity: 0.45 }} />
                              : <Circle size={15} />}
                          </button>
                        );
                      })()}

                      {/* Kebab — visible on row hover */}
                      <PortalDropdown
                        trigger={
                          <button className="tx-kebab" title="More actions">
                            <MoreHorizontal size={15} />
                          </button>
                        }
                        align="right"
                        onOpen={(close) => { activeDropdownRef.current = { close }; }}
                      >
                        {/* Status */}
                        {(() => {
                          const isCategorized = hasCategory || hasSplitsWithCategories;
                          return (
                            <button
                              className="dropdown-item"
                              onClick={() => isCategorized && handleVerificationToggle(transaction.id, !transaction.is_verified)}
                              disabled={!isCategorized}
                            >
                              {transaction.is_verified
                                ? <><XCircle size={14} /> Unverify</>
                                : <><CheckCircle size={14} /> Mark as verified</>}
                            </button>
                          );
                        })()}
                        {transaction.is_refund && (
                          <button className="dropdown-item" onClick={() => handleUnlinkRefund(transaction.id)}>
                            <RotateCcw size={14} /> Unlink refund
                          </button>
                        )}
                        <button className="dropdown-item" onClick={() => handleBusinessToggle(transaction.id, !transaction.is_business_expense)}>
                          {transaction.is_business_expense
                            ? <><User size={14} /> Mark as personal</>
                            : <><Briefcase size={14} /> Mark as business</>}
                        </button>
                        <div className="dropdown-divider" />
                        {/* Actions */}
                        {!isIncome && (
                          <button className="dropdown-item" onClick={() => { closeActiveDropdown(); setSplitModal({ isOpen: true, transaction }); }}>
                            <Scissors size={14} />
                            {transaction.splits && transaction.splits.length > 0
                              ? `Manage splits (${transaction.splits.length})`
                              : 'Split expense'}
                          </button>
                        )}
                        {isIncome && (
                          <button className="dropdown-item" onClick={() => handleOpenInsuranceModal(transaction)}>
                            <HeartPulse size={14} />
                            {transaction.is_reimbursable ? 'Manage reimbursement' : 'Mark as reimbursement'}
                          </button>
                        )}
                        <button className="dropdown-item" onClick={() => { closeActiveDropdown(); setRuleCreatorModal({ isOpen: true, transaction }); }}>
                          <Zap size={14} /> Create rule
                        </button>
                        <div className="dropdown-divider" />
                        {/* Info */}
                        <button className="dropdown-item" onClick={() => handleOpenDetails(transaction)}>
                          <FileText size={14} /> View details
                        </button>
                        <button className="dropdown-item" onClick={() => handleOpenDetails(transaction, 'notes')}>
                          <StickyNote size={14} /> {transaction.notes ? 'Edit notes' : 'Add notes'}
                        </button>
                        <div className="dropdown-divider" />
                        {/* Danger */}
                        <button className="dropdown-item delete-item" onClick={() => handleDeleteTransaction(transaction.id)}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </PortalDropdown>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {transactions.length === 0 && (
          <EmptyState
            icon="🔄"
            headline="No transactions yet"
            description="Connect a bank account to automatically import and categorize your transactions."
            primaryAction={{ label: 'Connect a Bank', onClick: () => navigate('/banks') }}
            secondaryAction={{ label: 'Import CSV', onClick: () => setCsvImportOpen(true) }}
          />
        )}
        {transactions.length > 0 && filteredTransactions.length === 0 && (
          <EmptyState
            icon="🔍"
            headline="No matching transactions"
            description="Your filters don't match any transactions in this date range."
            primaryAction={{
              label: 'Clear filters',
              onClick: () => {
                setInlineFilters({ dateRange: '', description: '', accounts: [], categories: [], amountMin: '', amountMax: '' });
                setStatusFilters([]);
                setStickyVisibleTransactions(new Set());
              }
            }}
          />
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '20px 0 8px' }}>
          <button
            className="btn btn-secondary"
            onClick={loadMoreTransactions}
            disabled={loadingMore}
            style={{ minWidth: '180px' }}
          >
            {loadingMore ? 'Loading…' : 'Load more transactions'}
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
            {transactions.length.toLocaleString()} loaded
          </span>
        </div>
      )}

      {/* Bulk Categorize Modal */}
      {(() => {
        const selectedArr = transactions.filter(t => selectedTransactions.has(t.id));
        const bulkIsIncome = selectedArr.length > 0 && selectedArr.every(t => t.amount > 0);
        const bulkCategories = bulkIsIncome ? incomeCategories : expenseCategories;
        return (
      <Dialog open={bulkCategorizeModal.isOpen} onOpenChange={(open) => { if (!open) setBulkCategorizeModal({ isOpen: false, categoryId: null, subcategoryId: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Categorize {selectedTransactions.size} transaction{selectedTransactions.size !== 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '8px 0 16px' }}>
            <CategorySubcategorySelect
              categories={bulkCategories}
              currentCategoryId={bulkCategorizeModal.categoryId}
              currentSubcategoryId={bulkCategorizeModal.subcategoryId}
              isIncome={bulkIsIncome}
              onChange={(categoryId, subcategoryId) =>
                setBulkCategorizeModal(prev => ({ ...prev, categoryId, subcategoryId }))
              }
              placeholder="Choose a category..."
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setBulkCategorizeModal({ isOpen: false, categoryId: null, subcategoryId: null })}>
              Cancel
            </Button>
            <Button
              disabled={bulkCategorizeModal.categoryId === null}
              onClick={() => handleBulkCategorize(bulkCategorizeModal.categoryId, bulkCategorizeModal.subcategoryId)}
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.isOpen} onOpenChange={(open) => { if (!open) setDeleteConfirm({ isOpen: false, ids: [] }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteConfirm.ids.length === 1 ? 'transaction' : `${deleteConfirm.ids.length} transactions`}?</DialogTitle>
          </DialogHeader>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '8px 0 20px' }}>
            This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setDeleteConfirm({ isOpen: false, ids: [] })}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={handleCloseDetails}
        transaction={detailsModal.transaction}
        onUpdate={handleTransactionUpdate}
        initialTab={detailsModal.initialTab}
      />

      {/* Insurance Reimbursement Modal */}
      <InsuranceReimbursementModal
        isOpen={insuranceModal.isOpen}
        onClose={handleCloseInsuranceModal}
        transaction={insuranceModal.transaction}
        onUpdate={async () => {
          await loadData(true);
        }}
      />

      {/* Transfer Detection Modal */}
      <TransferDetectionModal
        isOpen={transferModal.isOpen}
        onClose={handleCloseTransferModal}
        transaction={transferModal.transaction}
        potentialTransfers={transferModal.potentialTransfers}
        onConfirmTransfer={handleConfirmTransfer}
      />

      {/* Simple Refund Modal */}
      <SimpleRefundModal
        isOpen={refundPickerTarget !== null}
        onClose={handleCloseRefundPicker}
        transaction={refundPickerTarget}
        categories={expenseCategories}
        onConfirm={(categoryId, subcategoryId) => {
          if (refundPickerTarget) {
            handleRefundCategoryPicked(
              refundPickerTarget,
              categoryId,
              subcategoryId
            );
          }
        }}
      />

      {/* Split Expense Modal */}
      <SplitExpenseModal
        isOpen={splitModal.isOpen}
        onClose={() => setSplitModal({ isOpen: false, transaction: null })}
        transaction={splitModal.transaction}
        categories={categories}
        onSplitsUpdated={async () => {
          await loadData(true);
        }}
      />

      {/* Rule Creator Modal */}
      <RuleCreatorModal
        isOpen={ruleCreatorModal.isOpen}
        onClose={() => setRuleCreatorModal({ isOpen: false, transaction: null })}
        onRuleCreated={async () => {
          // Rule created successfully - refresh data to show all newly categorized transactions
          // This preserves scroll position while updating all affected transactions
          await loadData(true);
        }}
        onActionMessage={(msg) => {
          setActionError(null);
          setActionMessage(msg);
        }}
        transaction={ruleCreatorModal.transaction}
        prefillCategoryId={ruleCreatorModal.prefillCategoryId}
        prefillSubcategoryId={ruleCreatorModal.prefillSubcategoryId}
        prefillCondition={ruleCreatorModal.prefillCondition}
        categories={categories}
      />

      {/* PDF Import Modal */}
      <PDFImportModal
        isOpen={pdfImportOpen}
        onClose={() => setPdfImportOpen(false)}
        onImportComplete={() => {
          loadData();
          setPdfImportOpen(false);
        }}
        accounts={availableAccounts}
        onLoadAccounts={loadAccounts}
      />

      {/* PDF Debug Modal */}
      {pdfDebugOpen && (
        <PDFDebugModal
          isOpen={pdfDebugOpen}
          onClose={() => setPdfDebugOpen(false)}
        />
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImportComplete={() => { setCsvImportOpen(false); loadData(); }}
        accounts={availableAccounts}
      />
    </div>
  );
};

export default Transactions;